<template>
    <div class="full-wrapper rfid-scan-wrapper" @click="focusHiddenInput">
        <CustomCard :props="{
            width: 500,
            outlined: true,
            loading: loading ? '#F88D4C' : false,
        }">
            <template v-slot:card-title>{{ $t("message.scanRFID") }}</template>
            <template v-slot:card-body>
                <div class="auth-form">
                    <div class="rfid-icon-container">
                        <v-icon size="100" color="#FF6B00">mdi-contactless-payment-circle</v-icon>
                    </div>
                    <p class="text-center mb-4">{{ $t("message.pleaseScanRFID") }}</p>
                    <input 
                        ref="hiddenInput"
                        type="text" 
                        v-model="inputBuffer" 
                        class="hidden-input" 
                        @keyup.enter="checkBuffer"
                        autocomplete="off"
                    />
                    <div v-if="error" class="error-message text-red text-center mb-4">{{ error }}</div>
                    
                    <!-- Visual indicator for active scanning -->
                    <div class="scan-indicator" :class="{ 'scan-active': recentInput }"></div>
                    
                    <!-- Debug panel -->
                    <div v-if="showDebug" class="debug-panel">
                        <div class="debug-title">Debug Information</div>
                        <div><strong>Input Buffer:</strong> {{ inputBuffer || '(empty)' }}</div>
                        <div><strong>Recent Input:</strong> {{ recentInput ? 'Yes' : 'No' }}</div>
                        <div><strong>Last Key:</strong> {{ lastKey || '(none)' }}</div>
                        <div><strong>Last Scan:</strong> {{ lastScan || '(none)' }}</div>
                        <div><strong>Key Count:</strong> {{ keyCount }}</div>
                        <div><strong>Language Capture Blocked:</strong> {{ isLanguageMenuActive ? 'Yes' : 'No' }}</div>
                        <div class="debug-log">
                            <div v-for="(log, index) in debugLogs" :key="index">{{ log }}</div>
                        </div>
                    </div>
                    
                    <!-- Debug toggle button -->
                    <div class="debug-toggle" @click.stop="toggleDebug">
                        <v-icon small>{{ showDebug ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    </div>
                </div>
            </template>
        </CustomCard>
    </div>
</template>

<script>
import { ref, onMounted, nextTick, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import ButtonSuccess from "@components/Shared/Buttons/ButtonSuccess.vue";
import CustomCard from "@components/Shared/CustomCard.vue";

export default {
    name: "RFIDScan",
    components: {
        ButtonSuccess,
        CustomCard,
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const { t } = useI18n();

        // Get current instance to access global properties/methods
        const instance = getCurrentInstance();
        const { proxy } = instance;

        const rfid = ref('');
        const loading = ref(false);
        const error = ref(null);
        const hiddenInput = ref(null);
        const inputBuffer = ref('');
        const isLanguageMenuActive = ref(false);
        
        // Debugging state
        const showDebug = ref(false);
        const lastKey = ref('');
        const lastScan = ref('');
        const keyCount = ref(0);
        const recentInput = ref(false);
        const debugLogs = ref([]);
        let inputTimeout = null;
        
        const addDebugLog = (message) => {
            const timestamp = new Date().toLocaleTimeString();
            const logMessage = `${timestamp}: ${message}`;
            
            // Add to UI log
            debugLogs.value.unshift(logMessage);
            
            // Also log to console
            console.log(`[RFID Scanner] ${logMessage}`);
            
            // Keep logs to a reasonable size
            if (debugLogs.value.length > 20) {
                debugLogs.value = debugLogs.value.slice(0, 20);
            }
        };
        
        const toggleDebug = () => {
            showDebug.value = !showDebug.value;
        };

        // Check if an element is part of a language dropdown
        const isLanguageSelector = (element) => {
            if (!element) return false;
            
            return (
                // Check for Vuetify dropdown components
                element.classList?.contains('v-list-item') ||
                element.classList?.contains('v-menu') ||
                element.classList?.contains('v-select') ||
                element.classList?.contains('v-select__selection') ||
                element.classList?.contains('v-list') ||
                element.classList?.contains('v-overlay') ||
                element.closest('.v-menu') !== null ||
                element.closest('.v-list') !== null ||
                element.closest('.v-select') !== null ||
                element.closest('.v-overlay') !== null ||
                // Check for the SelectLanguage component
                element.classList?.contains('language') ||
                element.closest('.language') !== null
            );
        };

        // Check the entire document for active dropdowns
        const checkForActiveLanguageMenu = () => {
            // Look for any open Vuetify menu or overlay
            const activeMenu = document.querySelector('.v-menu--active') || 
                              document.querySelector('.v-overlay--active');
            
            // Set active state if found
            isLanguageMenuActive.value = !!activeMenu;
            return isLanguageMenuActive.value;
        };

        const focusHiddenInput = async (event) => {
            // Check if the menu is active anyway
            if (checkForActiveLanguageMenu()) {
                addDebugLog("Focus prevented - language menu active");
                return;
            }
            
            // Skip focus if we're clicking on language selector
            if (event && event.target && isLanguageSelector(event.target)) {
                addDebugLog("Focus prevented - clicked on language selector");
                return;
            }
            
            await nextTick();
            if (hiddenInput.value) {
                hiddenInput.value.focus();
            }
        };
        
        // Handle global keyboard input
        const handleKeyDown = (event) => {
            // First check if any language menu is active
            if (checkForActiveLanguageMenu()) {
                return;
            }
            
            // Skip key capture if clicking on a language selector element
            if (event.target && isLanguageSelector(event.target)) {
                return;
            }
            
            // Clear timeout and set recentInput for visual feedback
            if (inputTimeout) {
                clearTimeout(inputTimeout);
            }
            
            recentInput.value = true;
            inputTimeout = setTimeout(() => {
                recentInput.value = false;
            }, 1000);
            
            // Track debugging info
            lastKey.value = event.key;
            keyCount.value++;
            
            // Log key input for debugging
            if (showDebug.value) {
                addDebugLog(`Key: ${event.key} (${event.keyCode})`);
            }
            
            // Check for semicolon which indicates start/end of scan
            if (event.key === ';') {
                addDebugLog("Semicolon detected");
                
                // If starting a new scan
                if (!inputBuffer.value.startsWith(';')) {
                    inputBuffer.value = ';';
                    addDebugLog("Started new scan input");
                } 
                // If ending a scan
                else if (inputBuffer.value.startsWith(';') && !inputBuffer.value.endsWith(';')) {
                    inputBuffer.value += ';';
                    addDebugLog(`Complete input: ${inputBuffer.value}`);
                    checkBuffer();
                }
                // Don't add more than one semicolon on each end
                else {
                    addDebugLog("Ignoring extra semicolon");
                }
            } 
            // Add characters to buffer if we're in the middle of a scan
            else if (inputBuffer.value.startsWith(';') && !inputBuffer.value.endsWith(';')) {
                // Only allow spaces and hex characters (0-9, A-F)
                if (event.key === ' ' || /^[0-9A-Fa-f]$/.test(event.key)) {
                    inputBuffer.value += event.key;
                }
            }
        };

        // Process the input when the pattern is complete
        const checkBuffer = () => {
            addDebugLog(`Checking buffer: "${inputBuffer.value}"`);
            
            if (!inputBuffer.value) return;
            
            // Extract only hex characters from the input
            const hexPattern = /[0-9A-Fa-f]/g;
            const hexMatches = inputBuffer.value.match(hexPattern);
            
            // Check if we have hex characters and are enclosed by semicolons
            if (hexMatches && inputBuffer.value.includes(';')) {
                const uidHex = hexMatches.join('').toLowerCase();
                
                // Make sure we have a reasonable length for a UID (at least 4 characters)
                if (uidHex.length >= 4) {
                    console.log('RFID detected:', uidHex);
                    addDebugLog(`Valid RFID scan: ${uidHex} (${uidHex.length} characters)`);
                    lastScan.value = uidHex;
                    
                    rfid.value = uidHex;
                    inputBuffer.value = ''; // Clear the buffer
                    
                    // Process the RFID
                    submit();
                    return;
                }
            }
            
            // If we reached here, input wasn't valid
            if (inputBuffer.value.includes(';')) {
                console.log('Invalid RFID format:', inputBuffer.value);
                addDebugLog(`Could not extract valid hex data from input`);
                error.value = 'Invalid card format';
                inputBuffer.value = ''; // Clear the buffer
            }
        };

        const submit = () => {
            if (!rfid.value) return;
            loading.value = true;
            error.value = null;
            addDebugLog(`Submitting authentication for RFID: ${rfid.value}`);

            // Check if we're already authenticated (e.g. screen_view login already happened)
            const wasAuthenticated = store.getters["auth/getIsAuthenticated"];
            const initialRfidState = store.getters["auth/getIsRfidLogin"];

            store.dispatch('auth/rfidLogin', { rfid: rfid.value })
                .then(() => {
                    addDebugLog("RFID login successful");
                    return store.dispatch('auth/auth');
                })
                .then(() => {
                    addDebugLog("Auth fetch successful");
                    return store.dispatch('auth/permissions');
                })
                .then(() => {
                    addDebugLog("Permissions fetch successful");
                    proxy._showSuccessNotify(t("message.loginSuccess"));
                    router.push({ name: 'home' });
                })
                .catch((err) => {
                    console.error('[RFIDScan] Error during RFID login/auth/permissions chain:', err);
                    addDebugLog(`Authentication error: ${err.message || 'Unknown error'}`);
                    
                    // Restore the previous authentication state if we were already authenticated
                    if (wasAuthenticated && !store.getters["auth/getIsAuthenticated"]) {
                        // This handles the case where LOGIN_FAILURE cleared our state incorrectly
                        store.commit('auth/RESTORE_AUTH_STATE', { 
                            wasAuthenticated, 
                            wasRfidLogin: initialRfidState 
                        });
                    }
                    
                    // Check for 401 status code
                    if (err.response?.status === 401 || 
                        err.message?.includes('401') || 
                        (err.response?.data?.message && err.response.data.message.includes('401'))) {
                        error.value = 'Neatpazīta RFID bezkontaktu karte';
                    } else {
                        const errorMessage = err.response?.data?.message || 
                                         (err.message?.includes('AUTH_FAILURE') ? t('message.authFetchFailed') : 
                                         (err.message?.includes('PERMISSIONS_FAILURE') ? t('message.permissionsFetchFailed') : 
                                         t('message.rfidLoginFailed')));
                        error.value = errorMessage;
                    }
                    
                    rfid.value = '';
                    focusHiddenInput();
                })
                .finally(() => {
                    loading.value = false;
                });
        };

        onMounted(() => {
            // Ensure the hidden input is focused on load
            focusHiddenInput();
            
            // Set up global event listeners
            document.addEventListener('click', focusHiddenInput);
            document.addEventListener('keydown', handleKeyDown);
            
            // Set up observers for language menu activity
            document.addEventListener('mousedown', (event) => {
                if (event.target && isLanguageSelector(event.target)) {
                    isLanguageMenuActive.value = true;
                    addDebugLog("Language menu interaction detected");
                }
            });
            
            // Watch for clicks outside of language menu to reactivate scanning
            document.addEventListener('mouseup', (event) => {
                // Only check if we previously thought menu was active
                if (isLanguageMenuActive.value) {
                    // Give a small delay to ensure menu is fully closed
                    setTimeout(() => {
                        checkForActiveLanguageMenu();
                    }, 100);
                }
            });
            
            addDebugLog("RFID scan component mounted");
        });

        return {
            rfid,
            loading,
            error,
            hiddenInput,
            inputBuffer,
            submit,
            focusHiddenInput,
            checkBuffer,
            // Debug properties
            showDebug,
            toggleDebug,
            lastKey,
            lastScan,
            keyCount,
            recentInput,
            debugLogs,
            isLanguageMenuActive
        };
    },
    beforeUnmount() {
        // Clean up the global event listeners
        document.removeEventListener('click', this.focusHiddenInput);
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('mousedown', this.handleLanguageMenu);
        document.removeEventListener('mouseup', this.handleLanguageMenu);
    },
};
</script>

<style lang="scss" scoped>
@use '@/styles/_index' as *;

.rfid-scan-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh; /* Ensure it takes full viewport height */
    margin-top: -200px; /* Move the box 200px upward */
}
.auth-form {
    padding: 20px;
    position: relative;
}
.error-message {
    color: red;
    font-size: 0.9em;
}
.text-center {
    text-align: center;
}
.mb-4 {
    margin-bottom: 1rem;
}
.rfid-icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 1rem;
}
.hidden-input {
    position: absolute;
    opacity: 0;
    height: 0;
    width: 0;
    z-index: -1;
}

/* Debug elements */
.debug-panel {
    margin-top: 20px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.8em;
    text-align: left;
    background-color: #f5f5f5;
}
.debug-title {
    font-weight: bold;
    margin-bottom: 5px;
    text-align: center;
}
.debug-log {
    margin-top: 10px;
    max-height: 120px;
    overflow-y: auto;
    border-top: 1px solid #ddd;
    padding-top: 5px;
}
.debug-toggle {
    position: absolute;
    bottom: 5px;
    right: 5px;
    cursor: pointer;
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0.5;
    &:hover {
        opacity: 1;
    }
}

/* Scan indicator */
.scan-indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #ddd;
    position: absolute;
    top: 10px;
    right: 10px;
    transition: background-color 0.3s ease;
    
    &.scan-active {
        background-color: #4CAF50;
        box-shadow: 0 0 5px #4CAF50;
    }
}
</style> 